import React from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Typography, Button, Row, Col, Card } from 'antd';
import { ShoppingOutlined, HeartOutlined, SearchOutlined, LoginOutlined } from '@ant-design/icons';
import { useStores } from '../stores';
import AnimapLogo from '../assets/Animap_Logo_Color.png';
import PartnerMapPreview from '../components/PartnerMapPreview';
import './HomePage.scss';

const { Title, Paragraph } = Typography;

const HomePage = observer(() => {
  const navigate = useNavigate();
  const { authStore } = useStores();
  const { t } = useTranslation();

  const features = [
    {
      icon: <ShoppingOutlined />,
      title: t('home.features.products'),
      description: t('home.features.productsDesc'),
      action: () => navigate('/products'),
    },
    {
      icon: <HeartOutlined />,
      title: t('home.features.services'),
      description: t('home.features.servicesDesc'),
      action: () => navigate('/services'),
    },
    {
      icon: <SearchOutlined />,
      title: t('home.features.partners'),
      description: t('home.features.partnersDesc'),
      action: () => navigate('/partners'),
    },
  ];

  // Landing page for non-authenticated users
  if (!authStore.isAuthenticated) {
    return (
      <div className="home-page landing-page">
        <section className="hero-section landing-hero">
          <div className="hero-content sweet-container">
            <div className="hero-text bounce-in">
              <img src={AnimapLogo} alt="Animap" className="landing-logo" />
              <Title level={1} className="hero-title">
                {t('home.landing.title1')}
                <br />
                <span className="gradient-text">{t('home.landing.title2')}</span>
              </Title>
              <Paragraph className="hero-description">
                {t('home.landing.description')}
              </Paragraph>
              <div className="hero-actions">
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate('/register')}
                >
                  {t('home.landing.getStarted')}
                </Button>
                <Button
                  size="large"
                  icon={<LoginOutlined />}
                  onClick={() => navigate('/login')}
                >
                  {t('home.landing.logIn')}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Partner Map Section for landing page */}
        <PartnerMapPreview />
      </div>
    );
  }

  // Full homepage for authenticated users
  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content sweet-container">
          <div className="hero-text bounce-in">
            <Title level={1} className="hero-title">
              {t('home.authenticated.welcomeBack')}
              <br />
              <span className="gradient-text">{authStore.user?.first_name || t('home.authenticated.animalLover')}!</span>
            </Title>
            <Paragraph className="hero-description">
              {t('home.authenticated.description')}
            </Paragraph>
            <div className="hero-actions">
              <Button
                type="primary"
                size="large"
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/products')}
              >
                {t('home.authenticated.browseProducts')}
              </Button>
              <Button
                size="large"
                icon={<HeartOutlined />}
                onClick={() => navigate('/services')}
              >
                {t('home.authenticated.findServices')}
              </Button>
              <Button
                size="large"
                icon={<SearchOutlined />}
                onClick={() => navigate('/partners')}
              >
                {t('home.authenticated.findPartners')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Map Section */}
      <PartnerMapPreview />

      <section className="features-section">
        <div className="sweet-container">
          <Title level={2} className="section-title">
            {t('home.features.title')}
          </Title>
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} md={8} key={index}>
                <Card
                  className="feature-card"
                  hoverable
                  onClick={feature.action}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <Title level={4}>{feature.title}</Title>
                  <Paragraph>{feature.description}</Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>
    </div>
  );
});

export default HomePage;
